import { useCallback, useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Flex } from '@chakra-ui/react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { serialize } from 'object-to-formdata';
import HeaderNavigation from 'components/HeaderNavigation';
import { FORM_MAX_WIDTH } from 'utils/constants';
import FooterButtons, { FOOTER_HEIGHT } from 'components/FooterButtons';
import { BODY_SCHEMA, preparePayload } from 'pages/Attractions/Form/constants';
import ROUTES from 'app/routes';
import { API, APIRoutes } from 'api';
import { setFormErrors } from 'utils/api';
import ComponentPreview from 'components/PreviewWrapper';
import { SERIALIZER_OPTIONS } from 'app/constants';
import { convertKeysToSnakeCase } from 'utils/helpers';
import DndWrapper from 'components/DndWrapper';
import ContentComponentsForm from 'components/Content/ContentComponentsForm';
import AddButton from 'components/AddButton';
import FormBody from 'pages/Attractions/Form/components/FormBody';
import FormWrapper from 'components/FormWrapper';
import useQueryParams from 'utils/useQueryParams';

function AttractionCreate() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { queryString } = useQueryParams();
  let debounce: ReturnType<typeof setTimeout>;

  const backPath = useMemo(() => ROUTES.attractions.base + queryString, [queryString]);

  const methods = useForm<Attraction>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-restricted-types -- yup/resolver mismatch
    resolver: yupResolver(BODY_SCHEMA as unknown as Parameters<typeof yupResolver>[0]),
  });

  const { handleSubmit, setError, control } = methods;

  const {
    fields: containers,
    append,
    remove,
    move,
  } = useFieldArray({
    control,
    name: 'contentContainersAttributes',
    keyName: 'fieldId',
    shouldUnregister: true,
  });

  const attractionKind = useWatch({
    control,
    name: 'kind',
  });

  const addContainerHandler = useCallback(() => {
    append({ kind: '' } as ContentContainer);
  }, [append]);

  const reorderContainers = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      clearTimeout(debounce);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      debounce = setTimeout(() => move(dragIndex, hoverIndex), 500);
    },
    [move],
  );

  const { mutate: createAttraction, isLoading } = useMutation({
    mutationKey: 'attractionsMutation',
    mutationFn: (data: Attraction) => {
      const snakePayload = convertKeysToSnakeCase(preparePayload(data));
      const formData = serialize(snakePayload, SERIALIZER_OPTIONS, new FormData(), 'attraction');
      return API.post(APIRoutes.attractions.index(), formData);
    },
    onSuccess: () => {
      toast.success('Atrakcja została pomyślnie utworzona!');
      navigate(backPath);
      void queryClient.invalidateQueries('attractions');
    },
    onError: (error) => {
      setFormErrors(setError, error);
      toast.error('Wystąpił problem podczas tworzenia atrakcji');
    },
  });

  const onSubmit = handleSubmit((data: Attraction) => createAttraction(data));

  return (
    <Box>
      <HeaderNavigation
        baseCrumb={{
          label: 'Atrakcje',
          to: backPath,
        }}
        crumbs={[{ to: '', label: 'Nowa atrakcja' }]}
      />
      <FormWrapper {...methods}>
        <Flex gap={4} mb={4} alignItems="flex-start" pb={FOOTER_HEIGHT}>
          <Box w="100%" as="form" id="attraction-creator" onSubmit={onSubmit} maxW={FORM_MAX_WIDTH}>
            <FormBody isLoading={isLoading} />
            {containers.map((container, index) => (
              <DndWrapper
                key={`attraction-creator-${container.fieldId}`}
                id={container.fieldId}
                isLoading={isLoading}
                index={index}
                reorderContainers={reorderContainers}
                removeContainer={remove}
                ContainerWrapper={ContentComponentsForm}
                multipleContainers
              />
            ))}
            {attractionKind && <AddButton addHandler={addContainerHandler} mt={2} />}
          </Box>
          <ComponentPreview kind="attraction" />
        </Flex>
      </FormWrapper>
      <FooterButtons isLoading={isLoading} isCreate formId="attraction-creator" onCancel={() => navigate(-2)} />
    </Box>
  );
}

export default AttractionCreate;
